import React from 'react';
import { Box, Card, CardContent, Chip, Divider, Rating, Tooltip, Typography } from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { ChatBubbleOutline as ChatBubbleIcon } from '@mui/icons-material';
import { TalkRoom } from '../../types/talk';
import { Nurse, NURSE_TYPE_THEME } from '../../types/nurse';
import { Patient, PATIENT_THEME } from '../../types/patient';
import TalkStatusChip from './TalkStatusChip';
import { useAuth } from '../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { DEVELOPER_THEME } from '../../types/common';
import { formatDate, formatDateWithTime, getHumanReadableTimeAgo, getRemainingDaysInfo } from '../../utils/dateUtils';
import TalkSessionTable from './TalkSessionTable';
import { TimeDisplay } from '../common';

interface TalkRoomCardProps {
  room: TalkRoom;
  nurse?: Nurse | null;
  patient?: Patient | null;
}

const TalkRoomCard: React.FC<TalkRoomCardProps> = ({ room, nurse, patient }) => {
  const { isDeveloper } = useAuth();
  const navigate = useNavigate();
  const displayNurse = nurse || room.nurse;
  const displayPatient = patient || room.patient;
  const getFormattedDate = () => {
    if (room.talk_status === 'CANCELED') {
      return '';
    }
    // 新データ形式（sessions配列がある）に対応
    if (room.sessions && room.sessions.length > 0) {
      // 開始日と終了日を取得
      const startDates = room.sessions
      .map(session => session.start_at ? new Date(session.start_at).getTime() : 0)
      .filter(timestamp => timestamp > 0);
      const endDates = room.sessions
      .map(session => session.end_at ? new Date(session.end_at).getTime() : 0)
      .filter(timestamp => timestamp > 0);
      // 最古の開始日と最新の終了日を取得
      const oldestStartDate = startDates.length > 0 ? Math.min(...startDates) : null;
      const latestEndDate = endDates.length > 0 ? Math.max(...endDates) : null;
      let displayText = '';
      if (oldestStartDate) {
        const startDateISO = new Date(oldestStartDate).toISOString();
        // 対話期間表示は常に元の形式を維持
        displayText += formatDate(startDateISO);
        if (latestEndDate) {
          const endDateISO = new Date(latestEndDate).toISOString();
          // 対話期間表示は常に元の形式を維持
          displayText += ' 〜 ' + formatDate(endDateISO);
        } else {
          displayText += ' 〜 継続中';
        }
        return displayText;
      }
    }
    // v1のデータ形式（sessionsなし）
    else if (room.start_at) {
      let displayText = formatDate(room.start_at);
      if (room.end_at) {
        displayText += ' 〜 ' + formatDate(room.end_at);
      } else {
        displayText += ' 〜 継続中';
      }
      return displayText;
    }
    return '';
  };
  const getRemainingDaysDisplay = () => {
    if (room.talk_status === 'DOING') {
      let endDateStr: string | null = null;
      // 新データ形式（sessionsあり）
      if (room.sessions && room.sessions.length > 0) {
        // 最新の終了日を取得
        const endDates = room.sessions
        .map(session => session.end_at ? new Date(session.end_at).getTime() : 0)
        .filter(timestamp => timestamp > 0);
        endDateStr = endDates.length > 0 ? new Date(Math.max(...endDates)).toISOString() : null;
      }
      // v1のデータ形式
      else if (room.end_at) {
        endDateStr = room.end_at;
      }
      if (endDateStr) {
        const remainingInfo = getRemainingDaysInfo(endDateStr);
        if (remainingInfo.status === '残り' && remainingInfo.days !== null) {
          return (
            <Typography
              variant="body2"
              sx={{
                fontSize: '0.8rem',
                color: 'text.secondary'
              }}
            >
              ( 残り: {remainingInfo.days}日 )
            </Typography>
          );
        } else if (remainingInfo.status === '最終日') {
          return (
            <Typography
              variant="body2"
              sx={{
                fontSize: '0.8rem',
                fontWeight: 'bold',
                color: 'text.primary'
              }}
            >
              ( 最終日 )
            </Typography>
          );
        } else if (remainingInfo.status === '終了') {
          return (
            <Typography
              variant="body2"
              sx={{
                fontSize: '0.8rem',
                color: 'text.secondary'
              }}
            >
              ( 終了 )
            </Typography>
          );
        }
      }
    }
    return null;
  };
  return (
    <Card key={room.id} sx={{ mb: 4, boxShadow: 2, borderRadius: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TalkStatusChip
              status={room.talk_status}
              isForceEnd={room.is_force_end}
            />

            {isDeveloper() && (
              <Typography
                variant="body2"
                sx={{
                  ml: 1.5,
                  fontFamily: 'monospace',
                  backgroundColor: DEVELOPER_THEME.BACKGROUND_ALPHA,
                  border: `1px dashed ${DEVELOPER_THEME.BORDER_ALPHA}`,
                  color: DEVELOPER_THEME.DARK,
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  fontSize: '0.85rem'
                }}
              >
                TalkRoomID: {room.id}
              </Typography>
            )}
          </Box>

          <Box>
            {room.rating !== undefined && room.rating !== null && (
              <Tooltip
                title={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      評価日時: {room.rated_at ? formatDateWithTime(room.rated_at, 'jp') : '-'}
                    </Typography>
                    <Typography variant="body2">
                      コメント: {room.rating_comment || '[未入力]'}
                    </Typography>
                  </Box>
                }
                arrow
                placement="top"
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Rating
                    value={room.rating}
                    readOnly
                    precision={1}
                    size="small"
                  />
                  {room.rating_comment && (
                    <ChatIcon
                      fontSize="small"
                      color="action"
                      sx={{
                        ml: 1,
                        fontSize: '1.1rem',
                        opacity: 0.8,
                        '&:hover': {
                          opacity: 1
                        }
                      }}
                    />
                  )}
                </Box>
              </Tooltip>
            )}
          </Box>
        </Box>

        {/* 最終メッセージ表示（キャンセル以外の場合のみ） */}
        {room.last_chat_message && (
          <Box sx={{
            mt: 2,
            p: 1.5,
            backgroundColor: 'rgba(0, 0, 0, 0.02)',
            borderRadius: 1,
            border: '1px solid rgba(0, 0, 0, 0.08)'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
              <Typography variant="body2" fontWeight="medium" color="text.secondary">
                最終メッセージ
              </Typography>
              {room.last_chat_send_user_id && displayNurse && displayPatient && (
                <>
                  <Chip
                    label={room.last_chat_send_user_id === displayNurse.user_id ? '看護師' : '患者'}
                    size="small"
                    sx={{
                      ml: 1,
                      height: '18px',
                      fontSize: '0.7rem',
                      backgroundColor: room.last_chat_send_user_id === displayNurse.user_id
                        ? NURSE_TYPE_THEME.NORMAL.BACKGROUND
                        : PATIENT_THEME.MYSELF.BACKGROUND,
                      color: room.last_chat_send_user_id === displayNurse.user_id
                        ? NURSE_TYPE_THEME.NORMAL.DARK
                        : PATIENT_THEME.MYSELF.DARK,
                      border: room.last_chat_send_user_id === displayNurse.user_id
                        ? `1px solid ${NURSE_TYPE_THEME.NORMAL.BORDER}`
                        : `1px solid ${PATIENT_THEME.MYSELF.BORDER}`,
                      fontWeight: 'normal'
                    }}
                  />
                  {room.last_chat_send_at && (
                    <Tooltip title={`最終チャット日時: ${formatDateWithTime(room.last_chat_send_at, 'jp')}`} arrow placement="top">
                      <Box sx={{ display: 'flex', alignItems: 'center', ml: 1, cursor: 'help' }}>
                        <Typography variant="caption" color="text.secondary">
                          {getHumanReadableTimeAgo(room.last_chat_send_at)}
                        </Typography>
                        <AccessTimeIcon
                          fontSize="small"
                          color="action"
                          sx={{
                            ml: 0.5,
                            fontSize: '0.9rem',
                            opacity: 0.7,
                            '&:hover': {
                              opacity: 1
                            }
                          }}
                        />
                      </Box>
                    </Tooltip>
                  )}
                </>
              )}
            </Box>
            {room.last_chat_message.length > 50 ? (
              <Tooltip title={room.last_chat_message} placement="top-start">
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography
                    variant="body2"
                    sx={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: 'calc(100% - 24px)'
                    }}
                  >
                    {`${room.last_chat_message.substring(0, 50)}...`}
                  </Typography>
                  <ChatBubbleIcon
                    fontSize="small"
                    sx={{
                      ml: 0.5,
                      color: 'text.secondary',
                      opacity: 0.7,
                      fontSize: '1rem'
                    }}
                  />
                </Box>
              </Tooltip>
            ) : (
              <Typography variant="body2">
                {room.last_chat_message}
              </Typography>
            )}
          </Box>
        )}

        <Divider sx={{ my: 2 }}/>

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          {/* 申込み日時（CANCELED以外の場合のみ表示） */}
          {room.talk_status !== 'CANCELED' && room.request_at && (
            <>
              <TimeDisplay
                timestamp={room.request_at}
                prefix="申込み日時"
                tooltipPrefix="申込み日時"
                tooltipPlacement="top"
                fontSize="0.875rem"
                color="text.secondary"
              />
              <Box sx={{ mx: 2 }}/>
            </>
          )}

          <Typography variant="subtitle1" fontWeight="bold" sx={{ mr: 1 }}>
            {getFormattedDate()}
          </Typography>

          {getRemainingDaysDisplay()}
        </Box>

        {room.talk_status === 'CANCELED' ? (
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {/* 料金表示 */}
              {room.price !== undefined && (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ width: '150px' }}>
                    プラン:
                  </Typography>
                  <Chip
                    label={room.price > 0 ? `${room.price.toLocaleString()}円` : '無料'}
                    size="small"
                    color={room.price > 0 ? "error" : "default"}
                    variant="outlined"
                    sx={{
                      ...(room.price > 0 ? {
                        fontWeight: 'bold',
                        borderColor: 'error.main',
                        color: 'error.main'
                      } : {})
                    }}
                  />
                </Box>
              )}

              {/* 申込日時 */}
              {room.request_at && (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ width: '150px' }}>
                    申込み日時:
                  </Typography>
                  <Typography variant="body2">
                    {getHumanReadableTimeAgo(room.request_at)} （ {formatDateWithTime(room.request_at, 'jp')} ）
                  </Typography>
                </Box>
              )}

              {/* キャンセル日時 */}
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ width: '150px' }}>
                  キャンセル日時:
                </Typography>
                <Typography variant="body2">
                  {getHumanReadableTimeAgo(room.updated_at)} （ {formatDateWithTime(room.updated_at, 'jp')} ）
                </Typography>

              </Box>
            </Box>
          </Box>
        ) : (
          <TalkSessionTable room={room}/>
        )}
      </CardContent>
    </Card>
  );
};
export default TalkRoomCard;
